import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import SeoHead from '../components/SeoHead';
import { getCmsPage, getCmsSettings } from '../lib/cms';

export default function ContactPage() {
  const [searchParams] = useSearchParams();
  const prefilledProduct = searchParams.get('produkt') || '';
  const [page, setPage] = useState(null);
  const [settings, setSettings] = useState(null);

  useEffect(() => {
    let isMounted = true;
    Promise.all([getCmsPage('/kontakt'), getCmsSettings()]).then(([p, s]) => {
      if (isMounted) {
        if (p) setPage(p);
        if (s) setSettings(s);
      }
    });
    return () => {
      isMounted = false;
    };
  }, []);

  const [formData, setFormData] = useState({
    navn: '',
    telefon: '',
    email: '',
    produkt: prefilledProduct,
    beskrivelse: '',
    website_url: '', // Honeypot
  });

  useEffect(() => {
    if (prefilledProduct) {
      setFormData((prev) => ({ ...prev, produkt: prefilledProduct }));
    }
  }, [prefilledProduct]);

  const [status, setStatus] = useState('idle'); // 'idle' | 'submitting' | 'success' | 'error'
  const [errorMessage, setErrorMessage] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('submitting');
    setErrorMessage('');

    try {
      const payload = {
        navn: formData.navn,
        telefon: formData.telefon,
        email: formData.email,
        emne: formData.produkt ? `Forespørgsel vedr. ${formData.produkt}` : 'Kontakt fra hjemmeside',
        produkt: formData.produkt,
        beskrivelse: formData.beskrivelse,
        website_url: formData.website_url, // Honeypot MUST be included
      };

      const res = await fetch('/_api/webform/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (res.ok) {
        setStatus('success');
        setFormData({
          navn: '',
          telefon: '',
          email: '',
          produkt: '',
          beskrivelse: '',
          website_url: '',
        });
      } else {
        const errorData = await res.json().catch(() => ({}));
        setErrorMessage(errorData.message || 'Der opstod en fejl under afsendelsen. Prøv venligst igen.');
        setStatus('error');
      }
    } catch (err) {
      console.error('Fejl ved kontaktformular submit:', err);
      setErrorMessage('Der opstod en netværksfejl. Ring venligst direkte på 75 73 18 61.');
      setStatus('error');
    }
  };

  return (
    <>
      <SeoHead
        title={page?.meta_title || "Kontakt - KROMIX A/S"}
        description={page?.meta_description || "Kontakt KROMIX A/S på Hedegårdvej 8, 7323 Give. Tlf. 75 73 18 61, email kromix@kromix.dk. CVR 34610002."}
      />

      {/* Hero Header */}
      <section className="bg-[#39463A] text-white py-14 sm:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl space-y-3">
            <span className="text-xs font-bold uppercase tracking-widest text-[#DED3C0] block">
              {page?.badge_tekst || 'Personlig kundeservice'}
            </span>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-white">
              {page?.overskrift || 'Kontakt KROMIX A/S'}
            </h1>
            <p className="text-base sm:text-lg text-[#FCFAF5]/90 leading-relaxed">
              {page?.undertekst || 'Vi skaffer resultater – rigtig ernæring, sund levevis. Har du spørgsmål til vores foder eller ønsker du faglig rådgivning? Vi er altid klar til at hjælpe.'}
            </p>
          </div>
        </div>
      </section>

      {/* Main Content Section */}
      <section className="py-14 sm:py-20 bg-[#F4EFE5]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-start">
            {/* Left Column: Direct Company Information on Soft Ivory */}
            <div className="lg:col-span-5 space-y-6">
              <div className="bg-[#FCFAF5] rounded-3xl p-8 border border-[#DED3C0] shadow-sm space-y-6">
                <div>
                  <span className="text-xs font-bold uppercase tracking-wider text-[#71806A]">
                    Virksomhedsoplysninger
                  </span>
                  <h2 className="text-2xl font-bold text-[#39463A] mt-1">
                    {settings?.virksomhedsnavn || 'KROMIX A/S'}
                  </h2>
                </div>

                <div className="space-y-4 text-sm text-[#39463A]/80">
                  {/* Adresse */}
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-xl bg-[#F4EFE5] border border-[#DED3C0] text-[#71806A] flex items-center justify-center shrink-0">
                      <i className="fa-solid fa-location-dot" aria-hidden="true" />
                    </div>
                    <div>
                      <strong className="text-[#39463A] block text-xs uppercase tracking-wider">Adresse</strong>
                      <p className="text-[#39463A]/80 mt-0.5">
                        {settings?.adresse || 'Hedegårdvej 8'}<br />
                        {settings?.postnummer_by || '7323 Give'}
                      </p>
                    </div>
                  </div>

                  {/* Telefon */}
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-xl bg-[#F4EFE5] border border-[#DED3C0] text-[#71806A] flex items-center justify-center shrink-0">
                      <i className="fa-solid fa-phone" aria-hidden="true" />
                    </div>
                    <div>
                      <strong className="text-[#39463A] block text-xs uppercase tracking-wider">Telefon</strong>
                      <a
                        href={`tel:${(settings?.telefon || '75 73 18 61').replace(/\s+/g, '')}`}
                        className="text-base font-bold text-[#39463A] hover:text-[#71806A] transition-colors whitespace-nowrap mt-0.5 inline-block"
                      >
                        {settings?.telefon || '75 73 18 61'}
                      </a>
                    </div>
                  </div>

                  {/* Email */}
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-xl bg-[#F4EFE5] border border-[#DED3C0] text-[#71806A] flex items-center justify-center shrink-0">
                      <i className="fa-solid fa-envelope" aria-hidden="true" />
                    </div>
                    <div>
                      <strong className="text-[#39463A] block text-xs uppercase tracking-wider">Email</strong>
                      <a
                        href={`mailto:${settings?.email || 'kromix@kromix.dk'}`}
                        className="text-sm font-semibold text-[#39463A] hover:text-[#71806A] transition-colors whitespace-nowrap mt-0.5 inline-block"
                      >
                        {settings?.email || 'kromix@kromix.dk'}
                      </a>
                    </div>
                  </div>

                  {/* CVR */}
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-xl bg-[#F4EFE5] border border-[#DED3C0] text-[#71806A] flex items-center justify-center shrink-0">
                      <i className="fa-solid fa-building" aria-hidden="true" />
                    </div>
                    <div>
                      <strong className="text-[#39463A] block text-xs uppercase tracking-wider">CVR-nummer</strong>
                      <p className="text-sm font-semibold text-[#39463A] whitespace-nowrap mt-0.5">
                        {settings?.cvr || '34610002'}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Opening / advice notice */}
                <div className="p-4 rounded-xl bg-[#DED3C0]/40 border border-[#DED3C0] text-xs text-[#39463A] space-y-1">
                  <p className="font-bold text-[#39463A]">Faglig vejledning</p>
                  <p className="text-[#39463A]/80">
                    Vi rådgiver gerne om det rigtige foder til netop din hund eller kats foderstand og aktivitetsniveau.
                  </p>
                </div>
              </div>
            </div>

            {/* Right Column: Contact Form on Soft Ivory */}
            <div className="lg:col-span-7">
              <div className="bg-[#FCFAF5] rounded-3xl p-8 sm:p-10 border border-[#DED3C0] shadow-sm">
                <div className="mb-6">
                  <span className="text-xs font-bold uppercase tracking-wider text-[#71806A]">
                    Skriv til os
                  </span>
                  <h2 className="text-2xl font-bold text-[#39463A] mt-1">
                    Send en besked til KROMIX
                  </h2>
                  <p className="text-xs text-[#39463A]/80 mt-1">
                    Udfyld formularen, så vender vi hurtigt tilbage til dig.
                  </p>
                </div>

                {/* Form Status Messages */}
                {status === 'success' ? (
                  <div className="p-8 rounded-2xl bg-[#39463A] text-white text-center space-y-4">
                    <div className="w-16 h-16 rounded-full bg-white/20 flex items-center justify-center mx-auto text-2xl">
                      <i className="fa-solid fa-check" aria-hidden="true" />
                    </div>
                    <h3 className="text-xl font-bold">Mange tak for din henvendelse!</h3>
                    <p className="text-sm text-[#FCFAF5]/90 max-w-md mx-auto">
                      Vi har modtaget din besked og besvarer den hurtigst muligt. Hvis din henvendelse haster, er du altid velkommen til at ringe på 75 73 18 61.
                    </p>
                    <button
                      type="button"
                      onClick={() => setStatus('idle')}
                      className="px-6 py-2.5 rounded-lg bg-[#FCFAF5] text-[#39463A] text-xs font-bold uppercase tracking-wider hover:bg-white transition-colors"
                    >
                      Send en ny besked
                    </button>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-4">
                    {/* Antispam Honeypot Field */}
                    <div className="sr-only absolute left-[-9999px]" aria-hidden="true">
                      <label htmlFor="website_url">Don't fill this field</label>
                      <input
                        type="text"
                        id="website_url"
                        name="website_url"
                        tabIndex={-1}
                        autoComplete="off"
                        value={formData.website_url}
                        onChange={handleChange}
                      />
                    </div>

                    {/* Pre-selected Product Notice if present */}
                    {formData.produkt && (
                      <div className="p-3 rounded-xl bg-[#FCFAF5] border border-[#DED3C0] flex items-center justify-between text-xs">
                        <span>
                          Forespørgsel vedrørende: <strong className="text-[#39463A]">{formData.produkt}</strong>
                        </span>
                        <button
                          type="button"
                          onClick={() => setFormData((prev) => ({ ...prev, produkt: '' }))}
                          className="text-[#39463A]/70 hover:text-[#71806A]"
                          title="Fjern tilknyttet produkt"
                        >
                          <i className="fa-solid fa-xmark" aria-hidden="true" />
                        </button>
                      </div>
                    )}

                    {/* Navn */}
                    <div>
                      <label htmlFor="navn" className="block text-xs font-bold uppercase tracking-wider text-[#39463A] mb-1.5">
                        Navn <span className="text-[#B98262]">*</span>
                      </label>
                      <input
                        type="text"
                        id="navn"
                        name="navn"
                        required
                        value={formData.navn}
                        onChange={handleChange}
                        placeholder="Dit fulde navn"
                        className="w-full px-4 py-3 rounded-xl bg-[#FCFAF5] border border-[#DED3C0] text-sm text-[#39463A] placeholder-[#39463A]/50 focus:outline-hidden focus:border-[#71806A] focus:ring-1 focus:ring-[#71806A]"
                      />
                    </div>

                    {/* Telefon & Email Grid */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label htmlFor="telefon" className="block text-xs font-bold uppercase tracking-wider text-[#39463A] mb-1.5">
                          Telefonnummer
                        </label>
                        <input
                          type="tel"
                          id="telefon"
                          name="telefon"
                          value={formData.telefon}
                          onChange={handleChange}
                          placeholder="F.eks. 75 73 18 61"
                          className="w-full px-4 py-3 rounded-xl bg-[#FCFAF5] border border-[#DED3C0] text-sm text-[#39463A] placeholder-[#39463A]/50 focus:outline-hidden focus:border-[#71806A] focus:ring-1 focus:ring-[#71806A]"
                        />
                      </div>

                      <div>
                        <label htmlFor="email" className="block text-xs font-bold uppercase tracking-wider text-[#39463A] mb-1.5">
                          E-mail adresse <span className="text-[#B98262]">*</span>
                        </label>
                        <input
                          type="email"
                          id="email"
                          name="email"
                          required
                          value={formData.email}
                          onChange={handleChange}
                          placeholder="din@email.dk"
                          className="w-full px-4 py-3 rounded-xl bg-[#FCFAF5] border border-[#DED3C0] text-sm text-[#39463A] placeholder-[#39463A]/50 focus:outline-hidden focus:border-[#71806A] focus:ring-1 focus:ring-[#71806A]"
                        />
                      </div>
                    </div>

                    {/* Beskrivelse / Besked */}
                    <div>
                      <label htmlFor="beskrivelse" className="block text-xs font-bold uppercase tracking-wider text-[#39463A] mb-1.5">
                        Besked / Beskrivelse <span className="text-[#B98262]">*</span>
                      </label>
                      <textarea
                        id="beskrivelse"
                        name="beskrivelse"
                        rows={5}
                        required
                        value={formData.beskrivelse}
                        onChange={handleChange}
                        placeholder="Skriv din besked her..."
                        className="w-full px-4 py-3 rounded-xl bg-[#FCFAF5] border border-[#DED3C0] text-sm text-[#39463A] placeholder-[#39463A]/50 focus:outline-hidden focus:border-[#71806A] focus:ring-1 focus:ring-[#71806A]"
                      />
                    </div>

                    {status === 'error' && (
                      <div className="p-3.5 rounded-xl bg-[#FEE2E2] border border-[#FCA5A5] text-xs text-[#B91C1C]">
                        {errorMessage}
                      </div>
                    )}

                    <button
                      type="submit"
                      disabled={status === 'loading'}
                      className="w-full py-4 px-6 rounded-xl bg-[#71806A] hover:bg-[#5C6A56] text-white font-bold text-xs uppercase tracking-wider transition-all shadow-md active:scale-98 disabled:opacity-60 flex items-center justify-center gap-2 cursor-pointer"
                    >
                      {status === 'submitting' ? (
                        <>
                          <i className="fa-solid fa-spinner fa-spin" aria-hidden="true" />
                          <span>Sender besked...</span>
                        </>
                      ) : (
                        <>
                          <i className="fa-solid fa-paper-plane text-white" aria-hidden="true" />
                          <span>Send henvendelse</span>
                        </>
                      )}
                    </button>
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
